/**
 * Bible visuelle Onyx — poses / angles packagés sous public/mascots/onyx/refs/
 * Le corps 3D affiche CES screens (pas un mesh inventé).
 */
import type { OnyxEmotion } from '../../config/onyxEmotions';
import type { OnyxPoseId } from './onyxReactions';

const REFS = '/mascots/onyx/refs';

export const ONYX_REF_FILES = {
  idle_crossed: `${REFS}/onyx_ref_idle_arms_crossed.png`,
  wave: `${REFS}/onyx_ref_wave_hello.png`,
  eureka: `${REFS}/onyx_ref_eureka_point.png`,
  eureka_alt: `${REFS}/onyx_ref_eureka_point_alt.png`,
  thinking: `${REFS}/onyx_ref_thinking_worried.png`,
  proud: `${REFS}/onyx_ref_happy_thumbs_up.png`,
  playful: `${REFS}/onyx_ref_playful_finger_guns.png`,
  hurt: `${REFS}/onyx_ref_angry_upset.png`,
  celebrate: `${REFS}/onyx_ref_celebrate_dance.png`,
  /** Sommeil initial — au lit (WebP resize, pas le PNG 1.4 Mo). */
  sleep_bed: `${REFS}/onyx_ref_sleep_bed.webp`,
  /** Pause debout (mouvement se lever). */
  sleep_standing: `${REFS}/onyx_ref_sleep_standing.webp`,
  sleep_standing_alt: `${REFS}/onyx_ref_sleep_standing_alt.webp`,
  wake_1_stretch: `${REFS}/onyx_ref_wake_1_stretch.webp`,
  wake_2_rub_eyes: `${REFS}/onyx_ref_wake_2_rub_eyes.webp`,
  wake_3_yawn: `${REFS}/onyx_ref_wake_3_yawn.webp`,
  /** Café — frame poke après se lever. */
  wake_coffee: `${REFS}/onyx_ref_wake_coffee.webp`,
  profile_left: `${REFS}/onyx_ref_profile_left_blank.png`,
  profile_right: `${REFS}/onyx_ref_profile_right_blank.png`,
  back: `${REFS}/onyx_ref_back_blank.png`,
} as const;

export type OnyxRefKey = keyof typeof ONYX_REF_FILES;

/** Pose runtime → sprite bible (face caméra UNIQUEMENT — jamais dos/profil en FAB). */
export const ONYX_POSE_REF: Record<OnyxPoseId, OnyxRefKey> = {
  idle_service: 'idle_crossed',
  listening: 'idle_crossed',
  /** Profil : regarde le chat / ce que l’user écrit. */
  looking: 'profile_right',
  /** Café silencieux (idle FAB). */
  coffee: 'wake_coffee',
  thinking: 'thinking',
  eureka: 'eureka',
  eureka_alt: 'eureka_alt',
  wave: 'wave',
  hurt: 'hurt',
  proud: 'proud',
  playful: 'playful',
  celebrate: 'celebrate',
  /** Sleep = au lit (statut initial FAB). */
  sleep: 'sleep_bed',
};

/** Émotion chat → pose bible (quand le controller force une émotion). */
export function emotionToPoseRef(emotion: OnyxEmotion): OnyxRefKey {
  switch (emotion) {
    case 'THINKING':
      return 'thinking';
    case 'HAPPY':
      return 'proud';
    case 'SURPRISED':
      return 'eureka';
    case 'CONFIDENT':
    case 'NEUTRAL':
      return 'idle_crossed';
    case 'PLAYFUL':
    case 'LAUGHING':
      return 'playful';
    case 'ANGRY':
    case 'ANNOYED':
      return 'hurt';
    case 'SAD':
    case 'DISAPPOINTED':
      return 'thinking';
    case 'SLEEPING':
      return 'sleep_bed';
    default:
      return 'idle_crossed';
  }
}

/** Angles profil/dos = bible tech only — NEVER as FAB / chat avatar. */
export const ONYX_ANGLE_REFS_ONLY = [
  ONYX_REF_FILES.profile_left,
  ONYX_REF_FILES.profile_right,
  ONYX_REF_FILES.back,
] as const;

export function onyxPoseTextureUrl(pose: OnyxPoseId): string {
  return ONYX_REF_FILES[ONYX_POSE_REF[pose]];
}

export function onyxEmotionPoseUrl(emotion: OnyxEmotion): string {
  return ONYX_REF_FILES[emotionToPoseRef(emotion)];
}

/** Prefetch all bible angles (FAB / wake). */
export function onyxAllRefUrls(): string[] {
  return Object.values(ONYX_REF_FILES);
}
